/** Public auth & onboarding help — routes, FAQ, field tips, and support contact. */

export const AUTH_HELP_SUPPORT_EMAIL = 'operations@hfr.com';

export const AUTH_HELP_PATHS = {
  hub: '/help',
  faq: '/help/faq',
};

export const AUTH_HELP_CATEGORIES = [
  'Choosing a role',
  'Manager participation',
  'Account approval',
  'Getting started',
  'Firm profile',
];

/** Registration form field tips — keyed for FieldHelpTip. */
export const AUTH_FIELD_HELP = {
  email: {
    title: 'Work email',
    body: 'This address is your HFRConnect username. We send a 6-digit verification code here before you can set a password. Use an inbox you check regularly.',
  },
  verificationCode: {
    title: 'Verification code',
    bullets: [
      'Enter the 6-digit code sent to your email (usually within a minute).',
      'Check spam/junk; messages come from noreply@hfrconnect.ai.',
      'Invitations from HFR may come from operations@hfr.com — safelist both if needed.',
    ],
  },
  roleOverview: {
    title: 'Choose your role',
    body: 'Select the option that matches how you will use HFRConnect. You cannot change this after registration.',
  },
  roleManager: {
    title: 'Firm Manager',
    bullets: [
      'For fund managers and advisory firms listing opportunities on HFRConnect.',
      'After signup you complete your firm profile; HFR reviews it before full dashboard access.',
      'Participation defaults to Deferred. Revenue share is optional in Settings — there is no upfront cost; fees apply only when an investment order is successfully fulfilled.',
    ],
  },
  roleAllocator: {
    title: 'Allocator',
    bullets: [
      'For institutional and qualified allocators browsing manager opportunities.',
      'After signup you complete your institution profile and accredited-investor declaration.',
      'HFR may review your account before you can access the full allocator dashboard.',
    ],
  },
  termsAndConditions: {
    title: 'Terms & Conditions',
    body: 'Required platform agreement for managers and allocators. Open the document to review before checking the box. Revenue share is optional later in Settings.',
  },
};

/** In-wizard firm profile help — titles and bullets for WizardHelpTip. */
export const FIRM_WIZARD_HELP = {
  profileCompleteness:
    'Complete as much detail as you can—the richer your firm profile, the stronger it appears to investors. Required fields are marked with *.',

  foundedYearHelper:
    'Optional — shown on your firm profile. Look Up often fills this automatically.',

  basicInfo: {
    aiAssistant: {
      title: 'AI Firm Assistant',
      bullets: [
        'Upload a firm profile, pitchbook, or similar document (PDF, Excel, or CSV).',
        'AI pre-fills Basic Info and other wizard steps; team members appear on the Team step.',
        'Fields filled from your upload show an orange border—review and edit before submitting.',
      ],
    },
    nameLookup: {
      title: 'Firm Name Lookup',
      bullets: [
        'Enter your firm name, then click Look Up to fill empty fields from known data.',
        'Review every value Look Up adds—nothing is submitted until you finish the wizard.',
        'If nothing is found, enter details manually or use Upload Firm Documents on Basic Info.',
      ],
    },
    logo: {
      title: 'Firm Logo',
      bullets: [
        'Upload a PNG, JPG, or SVG (PNG preferred, max 5 MB).',
        'Extract pulls a logo from your website field when a URL is entered.',
        'Remove clears the current logo so you can upload or extract again.',
      ],
    },
  },

  team: {
    onboarding: {
      title: 'Team members during setup',
      bullets: [
        'Upload firm documents on Basic Info to import team members automatically—they appear here on the Team step.',
        'Or click Add Team Member to enter people manually.',
        'Review names, titles, and bios before you submit your firm profile.',
      ],
    },
    postCreate: {
      title: 'AI Team Assistant',
      bullets: [
        'Upload team bios, CVs, or org charts (PDF, Excel, or CSV).',
        'Extracted members are added to your list—edit or remove any row before saving.',
        'Use Add Team Member for one-off entries or photos.',
      ],
    },
  },
};

export const AUTH_HELP_FAQ = [
  {
    slug: 'manager-vs-allocator',
    category: 'Choosing a role',
    question: 'What is the difference between Firm Manager and Allocator?',
    answer:
      'Firm Manager is for investment professionals at a fund or advisory firm who publish opportunities, manage firm data, and interact with allocators. Allocator is for institutional or qualified investors who search opportunities, express interest, and communicate with managers. Choose the role that matches your organization — it is set at registration.',
  },
  {
    slug: 'invited-by-email',
    category: 'Choosing a role',
    question: 'I was invited by email — which role do I pick?',
    answer:
      'Follow the link in your invitation. Manager invites pre-select Firm Manager; allocator invites pre-select Allocator. If you are unsure, contact the person who invited you or operations@hfr.com.',
  },
  {
    slug: 'can-i-change-role',
    category: 'Choosing a role',
    question: 'Can I change my role after registering?',
    answer:
      'No. Roles are fixed at registration. If you registered with the wrong role, contact operations@hfr.com before completing onboarding.',
  },
  {
    slug: 'when-billing-selected',
    category: 'Manager participation',
    question: 'When do managers choose a participation option?',
    answer:
      'Participation is optional. After you create your firm, status is Deferred. You can select revenue share anytime under Settings → Participation Model. Some firms invited under an offline revenue-share arrangement may already have an agreement on file.',
  },
  {
    slug: 'billing-rev-share',
    category: 'Manager participation',
    question: 'What is Revenue Share Agreement?',
    answer:
      'No upfront cost and no monthly platform fee. You e-sign a revenue share agreement in Settings. HFRConnect fees apply only when an investment order is successfully fulfilled — you pay when capital is committed through a completed order, not when listing or browsing.',
  },
  {
    slug: 'billing-deferred-status',
    category: 'Manager participation',
    question: 'What does Deferred participation mean?',
    answer:
      'Deferred means you have not signed a revenue share agreement yet. You can still publish approved opportunities. Select revenue share under Settings → Participation Model whenever you are ready.',
  },
  {
    slug: 'billing-offline-rev-share',
    category: 'Manager participation',
    question: 'I was told my firm already has a revenue share agreement',
    answer:
      'Some firms sign revenue share offline with HFR before using the portal. In that case you may only need to accept Terms & Conditions at login. Contact operations@hfr.com if your agreement is not showing under Settings.',
  },
  {
    slug: 'why-approval-required',
    category: 'Account approval',
    question: 'Why does HFR review my account?',
    answer:
      'HFRConnect connects managers and allocators in a regulated marketplace. Review confirms firm and institution details, reduces fraud, and protects all users. Both managers and allocators may be reviewed.',
  },
  {
    slug: 'approval-timeline',
    category: 'Account approval',
    question: 'How long does approval take?',
    answer:
      'Typically 24–48 hours after you submit your firm or institution profile. You will receive email from noreply@hfrconnect.ai when approved. Check spam if you do not see it.',
  },
  {
    slug: 'approval-while-pending',
    category: 'Account approval',
    question: 'Can I log in while my account is under review?',
    answer:
      'Managers: After submitting your firm profile you may be signed out until HFR completes review; sign in again once approved. Allocators: You may see a pending approval message at login until review is complete.',
  },
  {
    slug: 'after-approval-manager',
    category: 'Account approval',
    question: 'What happens after my firm is approved?',
    answer:
      'Sign in to access the manager dashboard. Participation defaults to Deferred; you can select revenue share later in Settings, then create and publish opportunities once your firm and listing content are approved.',
  },
  {
    slug: 'after-approval-allocator',
    category: 'Account approval',
    question: 'What happens after my allocator account is approved?',
    answer:
      'Sign in to browse opportunities, manage your institution profile, and use allocator features.',
  },
  {
    slug: 'email-verification-required',
    category: 'Getting started',
    question: 'Why do I need to verify my email before registering?',
    answer:
      'HFRConnect requires a valid email before you can set a password and create your account. This keeps accounts secure and ensures we can reach you with important notices.',
  },
  {
    slug: 'cant-find-verification-email',
    category: 'Getting started',
    question: "I didn't receive the verification code",
    answer:
      'Check your spam or junk folder. The code is sent from our automated sender (noreply@hfrconnect.ai). If you received an invite from operations@hfr.com, safelist both addresses. Wait a minute and use Resend if available.',
  },
  {
    slug: 'mark-not-spam',
    category: 'Getting started',
    question: 'How do I stop HFR emails going to junk?',
    answer:
      'Mark messages from operations@hfr.com (invitations) and noreply@hfrconnect.ai (verification and system notices) as Not Spam or move them to your inbox so future mail is delivered reliably.',
  },
  {
    slug: 'after-register-what-next',
    category: 'Getting started',
    question: 'What happens immediately after I create my account?',
    answer:
      'Managers are signed in and taken to complete your firm profile — HFR review begins only after you submit that form. Allocators are signed in to complete their institution profile and accredited-investor declaration.',
  },
  {
    slug: 'firm-name-lookup',
    category: 'Firm profile',
    question: "How does firm name lookup work? What if my firm isn't found?",
    answer:
      'Enter your firm name and use the Look Up button next to the firm name field to fill empty fields from known data. If nothing is found, complete the remaining fields manually or upload firm documents for AI extraction. Use the ? icon on the form for more detail.',
  },
  {
    slug: 'document-extraction',
    category: 'Firm profile',
    question: 'What documents can I upload? How accurate is extraction?',
    answer:
      'You can upload PDF, Excel, or CSV files (see the upload dialog for size limits). AI extraction pre-fills fields you should review and edit before submitting. Always verify firm name, address, and contact details.',
  },
  {
    slug: 'required-firm-fields',
    category: 'Firm profile',
    question: 'Which firm profile fields are required?',
    answer:
      'Required fields are marked with an asterisk (*) on each wizard step. Team members can be added later from Settings after your firm is approved.',
  },
  {
    slug: 'how-long-firm-review',
    category: 'Firm profile',
    question: 'How long does HFR firm review take after I submit?',
    answer:
      'Review typically takes 24–48 hours. You will receive an email when your firm profile is approved. Check spam if you do not see it.',
  },
  {
    slug: 'login-while-firm-pending',
    category: 'Firm profile',
    question: 'Can I log in while my firm is under review?',
    answer:
      'After you submit your firm profile, you may be signed out while HFR reviews your submission. Once approved, sign in again to access the full manager dashboard.',
  },
  {
    slug: 'after-firm-approved',
    category: 'Firm profile',
    question: 'What happens after HFR approves my firm?',
    answer:
      'Sign in to access the manager dashboard. Participation defaults to Deferred; you can select revenue share later in Settings, then create and publish opportunities once your firm and listing content are approved.',
  },
];

export function getFaqBySlug(slug) {
  return AUTH_HELP_FAQ.find((item) => item.slug === slug);
}

export function getFieldHelp(helpKey) {
  return AUTH_FIELD_HELP[helpKey] ?? null;
}
